"use client";

import { useRef, useEffect, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  type Variants,
} from "motion/react";

// Shared motion language — ONE grammar across the whole site.
const EASE = [0.16, 1, 0.3, 1] as const; // master out-expo curve
const DUR = 1.0; // gentle on a calm white theme

/** Fade + rise in when scrolled into view (once). */
export function Reveal({
  children,
  delay = 0,
  y = 18,
  className = "",
  as = "div",
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: "div" | "span" | "li" | "section";
}) {
  const MotionTag = motion[as] as typeof motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px -12% 0px" }}
      transition={{ duration: DUR, ease: EASE, delay }}
    >
      {children}
    </MotionTag>
  );
}

/** Stagger children that are <Reveal>-like; use with RevealItem. */
export function RevealGroup({
  children,
  className = "",
  stagger = 0.1,
}: {
  children: React.ReactNode;
  className?: string;
  stagger?: number;
}) {
  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: stagger } },
  };
  return (
    <motion.div
      className={className}
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
    >
      {children}
    </motion.div>
  );
}

export function RevealItem({
  children,
  className = "",
  y = 18,
}: {
  children: React.ReactNode;
  className?: string;
  y?: number;
}) {
  const item: Variants = {
    hidden: { opacity: 0, y },
    show: { opacity: 1, y: 0, transition: { duration: DUR, ease: EASE } },
  };
  return (
    <motion.div className={className} variants={item}>
      {children}
    </motion.div>
  );
}

/**
 * Mask-wipe headline — each line sits in an overflow-hidden clip and rises from
 * a hard baseline (translateY 115%→0), staggered. The site's signature move
 * (hero, testimonials, footer). Pass an array of lines (strings or nodes).
 */
export function MaskReveal({
  lines,
  className = "",
  lineClassName = "",
  stagger = 0.09,
  delay = 0,
  duration = 1.1,
  once = true,
}: {
  lines: React.ReactNode[];
  className?: string;
  lineClassName?: string;
  stagger?: number;
  delay?: number;
  duration?: number;
  once?: boolean;
}) {
  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: stagger, delayChildren: delay } },
  };
  const lineV: Variants = {
    hidden: { y: "108%" },
    show: { y: "0%", transition: { duration, ease: EASE } },
  };
  return (
    <motion.span
      className={`block ${className}`}
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once, margin: "-8% 0px -8% 0px" }}
    >
      {lines.map((ln, i) => (
        // pb gives descenders (g, y, p) room so the overflow clip never crops them
        <span key={i} className="block overflow-hidden pb-[0.2em]">
          <motion.span
            variants={lineV}
            className={`block will-change-transform ${lineClassName}`}
          >
            {ln}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

/**
 * Ken-Burns reveal image — settles from a gentle zoom + fade as it scrolls into
 * view (once). Works on web and mobile. `hover` adds a slow zoom on pointer
 * hover that composes with the reveal. Use inside an overflow-hidden, rounded
 * parent; pass sizing/object classes via className.
 */
export function RevealImage({
  src,
  alt = "",
  className = "",
  scaleFrom = 1.1,
  hover = false,
  cmsKey,
}: {
  src: string;
  alt?: string;
  className?: string;
  scaleFrom?: number;
  hover?: boolean;
  /** CMS content key — stamps data-cms-img for the admin in-place editor. */
  cmsKey?: string;
}) {
  return (
    <motion.img
      src={src}
      alt={alt}
      data-cms-img={cmsKey}
      loading="lazy"
      className={className}
      initial={{ scale: scaleFrom, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      whileHover={hover ? { scale: 1.04 } : undefined}
      viewport={{ once: true, margin: "-8% 0px -8% 0px" }}
      transition={{ duration: 1.4, ease: EASE }}
    />
  );
}

/** Vertical parallax: translates its child as the element scrolls through view. */
export function Parallax({
  children,
  amount = 60,
  className = "",
}: {
  children: React.ReactNode;
  amount?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [amount, -amount]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div style={{ y }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  );
}

/** Count-up number that animates the first time it scrolls into view. */
export function Counter({
  value,
  suffix = "",
  decimals = 0,
  className = "",
  immediate = false,
}: {
  value: number;
  suffix?: string;
  decimals?: number;
  className?: string;
  immediate?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inViewActual = useInView(ref, { once: true, margin: "0px 0px -8% 0px" });
  const inView = immediate || inViewActual;
  const fmt = (v: number) =>
    decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString();
  const [display, setDisplay] = useState(fmt(0));

  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    let start = 0;
    const duration = 1400;
    const step = (now: number) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(fmt(value * eased));
      if (t < 1) raf = requestAnimationFrame(step);
      else setDisplay(fmt(value)); // guarantee final value
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, value, decimals]);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
}

/** Infinite horizontal logo/text marquee. */
export function Marquee({
  children,
  speed = 28,
  className = "",
}: {
  children: React.ReactNode;
  speed?: number;
  className?: string;
}) {
  return (
    <div className={`group relative flex overflow-hidden ${className}`}>
      <motion.div
        className="flex shrink-0 items-center gap-16 pr-16"
        animate={{ x: ["0%", "-100%"] }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
      >
        {children}
      </motion.div>
      <motion.div
        aria-hidden
        className="flex shrink-0 items-center gap-16 pr-16"
        animate={{ x: ["0%", "-100%"] }}
        transition={{ duration: speed, ease: "linear", repeat: Infinity }}
      >
        {children}
      </motion.div>
    </div>
  );
}
